// src/belediye/redux/slices/belediyeSurveyReportsSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchSurveyReport,
  fetchSurveyReportSummary,
  fetchSurveyReportTimeseries,
} from "../../services/belediyeApi";

/**
 * DEBUG helper
 * - Prod'da gürültü olmasın diye REACT_APP_DEBUG_SURVEY_REPORTS ile kontrol edebilirsin.
 */
const DEBUG =
  String(process.env.REACT_APP_DEBUG_SURVEY_REPORTS ?? "true").toLowerCase() ===
  "true";

const log = (...args) => {
  if (!DEBUG) return;
  // eslint-disable-next-line no-console
  
};

const warn = (...args) => {
  if (!DEBUG) return;
  // eslint-disable-next-line no-console
  
};

const errorLog = (...args) => {
  if (!DEBUG) return;
  // eslint-disable-next-line no-console
  
};

const getErrDetail = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const message =
    data?.detail ||
    data?.message ||
    (typeof data === "string" ? data : null) ||
    err?.message ||
    fallback;

  return { message, status, data };
};

// UI'da card içine basınca [object Object] olmasın diye
const errToUi = (payloadOrString, fallback) => {
  if (!payloadOrString) return fallback;
  if (typeof payloadOrString === "string") return payloadOrString;
  if (typeof payloadOrString === "object" && payloadOrString.message) {
    const status = payloadOrString.status ? ` (HTTP ${payloadOrString.status})` : "";
    return `${payloadOrString.message}${status}`;
  }
  return fallback;
};

// Pagination / farklı response formatlarını normalize et
const normalizeSurveyList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.results)) return data.results;
  if (data && typeof data === "object" && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Anonimlik hedefi:
 * - UI tarafında kullanıcı bazlı metrikler gösterilmeyecek.
 * - Backend unique_users dönse bile burada UI için 0'a çekiyoruz (kırmadan).
 */
const anonymizeSurveySummaryRow = (row) => {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    unique_users: 0,
  };
};

const anonymizeSurveyReportPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;

  const totals =
    payload.totals && typeof payload.totals === "object"
      ? { ...payload.totals, unique_users: 0 }
      : payload.totals;

  return { ...payload, totals };
};

const anonymizeTimeseriesPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  const series = Array.isArray(payload.series)
    ? payload.series.map((p) => ({ ...p, unique_users: 0 }))
    : payload.series;
  return { ...payload, series };
};

const normalizeBucket = (bucket) => {
  const b = String(bucket || "day").toLowerCase().trim();
  if (b === "daily") return "day";
  if (b === "weekly") return "week";
  if (b === "monthly") return "month";
  if (b === "day" || b === "week" || b === "month") return b;
  return "day";
};

export const loadSurveysWithStats = createAsyncThunk(
  "belediyeSurveyReports/loadSurveysWithStats",
  async (params, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    log("THUNK loadSurveysWithStats:start", { params, hasToken: !!token });

    try {
      const from = params?.from ?? params?.date_from ?? undefined;
      const to = params?.to ?? params?.date_to ?? undefined;

      const apiParams = from || to ? { from, to } : undefined;

      log("THUNK loadSurveysWithStats:request", {
        endpoint: "/surveys/surveys/report-summary/",
        apiParams,
      });

      const data = await fetchSurveyReportSummary(apiParams);

      log("THUNK loadSurveysWithStats:success", {
        isArray: Array.isArray(data),
        hasResultsArray: !!(data && typeof data === "object" && Array.isArray(data.results)),
        preview: Array.isArray(data) ? data.slice(0, 2) : data?.results?.slice?.(0, 2) ?? data,
      });

      return data;
    } catch (err) {
      const e = getErrDetail(err, "Anket listesi alınamadı.");
      errorLog("THUNK loadSurveysWithStats:error", e);
      return rejectWithValue(e);
    }
  }
);

export const loadSurveyStats = createAsyncThunk(
  "belediyeSurveyReports/loadSurveyStats",
  async ({ surveyId, date_from, date_to }, { rejectWithValue }) => {
    log("THUNK loadSurveyStats:start", { surveyId, date_from, date_to });

    try {
      if (!surveyId) warn("THUNK loadSurveyStats:surveyId missing");

      const apiParams = {
        from: date_from ?? undefined,
        to: date_to ?? undefined,
      };

      log("THUNK loadSurveyStats:request", {
        endpoint: `/surveys/surveys/${surveyId}/report/`,
        apiParams,
      });

      const data = await fetchSurveyReport(surveyId, apiParams);

      log("THUNK loadSurveyStats:success", {
        keys: data && typeof data === "object" ? Object.keys(data) : null,
      });

      return data;
    } catch (err) {
      const e = getErrDetail(err, "Anket istatistikleri alınamadı.");
      errorLog("THUNK loadSurveyStats:error", e);
      return rejectWithValue(e);
    }
  }
);

/**
 * period/bucket desteği:
 * - day / week / month
 */
export const loadSurveyTimeseries = createAsyncThunk(
  "belediyeSurveyReports/loadSurveyTimeseries",
  async ({ surveyId, date_from, date_to, period = "day" }, { rejectWithValue }) => {
    const bucket = normalizeBucket(period);

    log("THUNK loadSurveyTimeseries:start", {
      surveyId,
      date_from,
      date_to,
      period,
      bucket,
    });

    try {
      if (!surveyId) warn("THUNK loadSurveyTimeseries:surveyId missing");

      const apiParams = {
        bucket,
        from: date_from ?? undefined,
        to: date_to ?? undefined,
      };

      log("THUNK loadSurveyTimeseries:request", {
        endpoint: `/surveys/surveys/${surveyId}/report-timeseries/`,
        apiParams,
      });

      const data = await fetchSurveyReportTimeseries(surveyId, apiParams);

      log("THUNK loadSurveyTimeseries:success", {
        keys: data && typeof data === "object" ? Object.keys(data) : null,
      });

      return { bucket, data };
    } catch (err) {
      const e = getErrDetail(err, "Zaman serisi alınamadı.");
      errorLog("THUNK loadSurveyTimeseries:error", e);
      return rejectWithValue(e);
    }
  }
);

const initialState = {
  surveys: [],
  surveysLoading: false,
  surveysError: null,

  stats: null,
  statsLoading: false,
  statsError: null,

  // Backward compat (eski sayfalar timeseries okuyor)
  timeseries: null,
  timeseriesLoading: false,
  timeseriesError: null,

  // Yeni: bucket bazlı sakla (day/week/month)
  timeseriesByBucket: {
    day: null,
    week: null,
    month: null,
  },
  activeBucket: "day",

  // Debug için ham error objesi
  _lastError: null,

  lastQuery: {
    date_from: null,
    date_to: null,
  },
};

const slice = createSlice({
  name: "belediyeSurveyReports",
  initialState,
  reducers: {
    clearSurveyReportsState: () => initialState,

    setSurveyDateRange: (state, action) => {
      state.lastQuery = {
        ...state.lastQuery,
        ...action.payload,
      };
      log("REDUCER setSurveyDateRange", state.lastQuery);
    },

    setActiveBucket: (state, action) => {
      state.activeBucket = normalizeBucket(action.payload);
      log("REDUCER setActiveBucket", state.activeBucket);

      // activeBucket değişince, mevcut cache varsa backward compat alanına da yansıt
      const cached = state.timeseriesByBucket?.[state.activeBucket] ?? null;
      state.timeseries = cached;
    },
  },
  extraReducers: (builder) => {
    builder
      // list
      .addCase(loadSurveysWithStats.pending, (state, action) => {
        state.surveysLoading = true;
        state.surveysError = null;
        state._lastError = null;
        log("REDUCER loadSurveysWithStats.pending", { arg: action.meta.arg });
      })
      .addCase(loadSurveysWithStats.fulfilled, (state, action) => {
        state.surveysLoading = false;

        const normalized = normalizeSurveyList(action.payload).map(anonymizeSurveySummaryRow);
        state.surveys = normalized;

        log("REDUCER loadSurveysWithStats.fulfilled", {
          count: state.surveys.length,
        });
      })
      .addCase(loadSurveysWithStats.rejected, (state, action) => {
        state.surveysLoading = false;
        state._lastError = action.payload || action.error;
        state.surveysError = errToUi(action.payload, "Anket listesi alınamadı.");

        errorLog("REDUCER loadSurveysWithStats.rejected", {
          payload: action.payload,
          error: action.error,
        });
      })

      // stats
      .addCase(loadSurveyStats.pending, (state, action) => {
        state.statsLoading = true;
        state.statsError = null;
        state._lastError = null;
        log("REDUCER loadSurveyStats.pending", { arg: action.meta.arg });
      })
      .addCase(loadSurveyStats.fulfilled, (state, action) => {
        state.statsLoading = false;

        // anonimlik: unique_users UI'da kullanılmasın
        state.stats = anonymizeSurveyReportPayload(action.payload);

        log("REDUCER loadSurveyStats.fulfilled", {
          hasStats: !!state.stats,
        });
      })
      .addCase(loadSurveyStats.rejected, (state, action) => {
        state.statsLoading = false;
        state._lastError = action.payload || action.error;
        state.statsError = errToUi(action.payload, "Anket istatistikleri alınamadı.");

        errorLog("REDUCER loadSurveyStats.rejected", {
          payload: action.payload,
          error: action.error,
        });
      })

      // timeseries
      .addCase(loadSurveyTimeseries.pending, (state, action) => {
        state.timeseriesLoading = true;
        state.timeseriesError = null;
        state._lastError = null;

        //   KRİTİK: UI bucket değişince timeseries alanı da değişsin
        const reqBucket = normalizeBucket(action.meta?.arg?.period);
        state.activeBucket = reqBucket;

        // varsa cache’i hemen göster (yoksa null)
        state.timeseries = state.timeseriesByBucket?.[reqBucket] ?? null;

        log("REDUCER loadSurveyTimeseries.pending", { arg: action.meta.arg, reqBucket });
      })
      .addCase(loadSurveyTimeseries.fulfilled, (state, action) => {
        state.timeseriesLoading = false;

        const bucket = normalizeBucket(action.payload?.bucket);
        const payload = anonymizeTimeseriesPayload(action.payload?.data);

        state.timeseriesByBucket = {
          ...state.timeseriesByBucket,
          [bucket]: payload,
        };

        //   activeBucket zaten pending'de set edildi; burada sync garanti
        if (state.activeBucket === bucket) {
          state.timeseries = payload;
        }

        log("REDUCER loadSurveyTimeseries.fulfilled", {
          bucket,
          activeBucket: state.activeBucket,
          hasTimeseries: !!payload,
          seriesLen: Array.isArray(payload?.series) ? payload.series.length : null,
        });
      })
      .addCase(loadSurveyTimeseries.rejected, (state, action) => {
        state.timeseriesLoading = false;
        state._lastError = action.payload || action.error;
        state.timeseriesError = errToUi(action.payload, "Zaman serisi alınamadı.");

        errorLog("REDUCER loadSurveyTimeseries.rejected", {
          payload: action.payload,
          error: action.error,
        });
      });
  },
});

export const { clearSurveyReportsState, setSurveyDateRange, setActiveBucket } =
  slice.actions;

export default slice.reducer;
